import React, { useState, useEffect } from "react";
import StepCard from "../../../../component/stepCard";
import PersonalInformation from "./personalInformation";
import "./userComponents.css";
import OtherDetails from "./otherDetails";
import AccountDetails from "./accountDetails";
import UploadDocument from "./uploadDocument";
import ButtonNavigationBar from "../../../../component/buttonNavigationBar";
import { useNavigate } from "react-router-dom";
import {
  buildPersonalInfoPayload,
  buildOtherDetailsPayload,
  buildBankDetailsPayload,
  buildUploadDocsPayload,
} from "../../../../utils/buildUserPayload";
import axiosInstance from "../../../../configuration/request";
import { useSnackbar } from "../../../../component/SnackbarContext";

const steps = [
  "Personal Information",
  "Other Details",
  "Bank Details",
  "Upload Documents",
];
function DeliveryManagement({
  handleProgress,
  user = "",
  mode = "create",
  id = "",
  userDetails,
  updateUser,
}) {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [activeStepTitle, setActiveStepTitle] = useState(
    "Personal Information"
  );
  const [userPayload, setUserPayload] = useState({
    personal: {},
    otherDetails: {},
    bankDetails: {},
    uploadDocs: {},
  });

  useEffect(() => {
    if (userDetails) {
      setUserPayload({
        personal: userDetails?.personal || {},
        otherDetails: userDetails?.otherDetails || {},
        bankDetails: userDetails?.bankDetails || {},
        uploadDocs: userDetails?.uploadDocs || [],
      });
    }
  }, [userDetails]);

  const handleNext = (formData, stepKey) => {
    if (stepKey !== "upload-doc") {
      const progress = Math.round(
        ((activeStep + 1) / (steps.length - 1)) * 100
      );
      setActiveStep((prev) => prev + 1);
      setActiveStepTitle(steps[activeStep + 1]);
      handleProgress(progress);
    }

    let newPayload;
    setUserPayload((prev) => {
      if (stepKey === "personal-info") {
        newPayload = { ...prev, personal: formData };
      } else if (stepKey === "otherDetails") {
        newPayload = { ...prev, otherDetails: formData };
      } else if (stepKey === "bank-details") {
        newPayload = { ...prev, bankDetails: formData };
      } else if (stepKey === "upload-doc") {
        newPayload = { ...prev, uploadDocs: formData };
      } else {
        newPayload = prev;
      }
      return newPayload;
    });
    if (mode === "edit" && formData && Object.keys(formData).length > 0) {
      editUser(formData, stepKey);
    } else if (stepKey === "upload-doc") {
      // Slight delay to ensure newPayload gets set correctly
      Promise.resolve().then(() => {
        createUser(newPayload);
      });
    }
  };

  const handlePrevious = () => {
    if (activeStep !== 0) {
      const progress = Math.round(
        ((activeStep - 1) / (steps.length - 1)) * 100
      );
      setActiveStepTitle(steps[activeStep - 1]);
      setActiveStep((prev) => prev + -1);
      handleProgress(progress);
    }
  };
  const stepHandler = (step) => {
    setActiveStep(step);
    setActiveStepTitle(steps[step]);
  };

  const editUser = (formData, stepKey = null) => {
    let payloadData;
    switch (stepKey) {
      case "personal-info":
        payloadData = buildPersonalInfoPayload(formData);
        break;
      case "upload-doc":
      case "otherDetails":
        payloadData = buildOtherDetailsPayload(
          formData,
          userPayload.uploadDocs
        );
        payloadData = payloadData = buildOtherDetailsPayload({
          otherDetails:
            stepKey === "otherDetails" ? formData : userPayload.otherDetails,
          uploadedDocs:
            stepKey === "upload-doc" ? formData : userPayload.uploadDocs,
          id: userPayload?.otherDetails?.id,
        });
        break;
      case "bank-details":
        payloadData = buildBankDetailsPayload(formData);
        break;
      default:
        return;
    }
    axiosInstance
      .patch(`customer/users/${id}`, payloadData)
      .then(() => {
        if (stepKey === "upload-doc") {
          navigate(`/app/user-management`);
        } else {
          updateUser();
        }
      })
      .catch((error) => {
        if (stepKey === "upload-doc") {
            setActiveStep(3);
            setActiveStepTitle("Upload Documents")
            handleProgress(100)
        }
        if (error?.response?.data?.message) {
          showSnackbar(error?.response?.data?.message, "error");
        } else {
          showSnackbar("Something went wrong!", "error");
        }
      });
  };

  const createUser = (payload) => {
    let payloadData = {
      ...(payload.personal &&
        Object.keys(payload.personal).length > 0 &&
        buildPersonalInfoPayload(payload.personal)),
      ...buildOtherDetailsPayload({
        otherDetails: payload.otherDetails,
        uploadedDocs: payload.uploadDocs,
      }),
      ...(payload.bankDetails &&
        Object.keys(payload.bankDetails).length > 0 &&
        buildBankDetailsPayload(payload.bankDetails)),
      userType: "delivery user",
      isApproved: "approved",
      roles: [],
    };

    axiosInstance
      .post("/customer/users", payloadData)
      .then(() => {
        navigate(`/app/user-management`);
      })
      .catch((error) => {
        if (error?.response?.data?.message) {
          showSnackbar(error?.response?.data?.message, "error");
        } else {
          showSnackbar("Something went wrong!", "error");
        }
      });
  };

  return (
    <div>
      {mode === "create" && (
        <div className="shadow-card py-7 px-3 mx-2 mb-5 rounded-full border border-[#e8e5e5]">
          <StepCard activeStep={activeStep} steps={steps} />
        </div>
      )}
      {mode === "edit" && (
        <div className="max-w-[680px] mx-2 mb-5">
          <ButtonNavigationBar
            activeStep={activeStep}
            steps={steps}
            handleSteps={stepHandler}
          />
        </div>
      )}
      {user === "admin" && (
        <div className="font-[500] text-[26px] mb-1 ml-3 text-[var(--primary-text-color)]">
          {steps[activeStep]}
        </div>
      )}
      <div
        className={`form-container p-2 ${
          mode === "edit" ? "h-[calc(100vh-265px)]" : "h-[calc(100vh-290px)]"
        }`}
      >
        {activeStepTitle === "Personal Information" && (
          <PersonalInformation
            nextHandler={handleNext}
            previousHandler={handlePrevious}
            mode={mode}
            formValues={userPayload.personal}
          />
        )}
        {activeStepTitle === "Other Details" && (
          <OtherDetails
            nextHandler={handleNext}
            previousHandler={handlePrevious}
            mode={mode}
            formValues={userPayload.otherDetails}
          />
        )}
        {activeStepTitle === "Bank Details" && (
          <AccountDetails
            nextHandler={handleNext}
            previousHandler={handlePrevious}
            mode={mode}
            formValues={userPayload.bankDetails}
          />
        )}
        {activeStepTitle === "Upload Documents" && (
          <UploadDocument
            mode={mode}
            uploadedDocs={userPayload.uploadDocs}
            nextHandler={handleNext}
            previousHandler={handlePrevious}
          />
        )}
      </div>
    </div>
  );
}

export default DeliveryManagement;
